import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { NativeSelect } from '@/components/ui/native-select'
import { toastFailure } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { useTeamMembers } from '@/features/preinvest/useTeamMembers'
import { CategorySelect } from '../components/CategorySelect'
import { LabelChip } from '../components/LabelChip'
import { LabelPicker } from '../components/LabelPicker'
import { listChoices } from '../lists/api'
import { ListDialog } from '../lists/ListDialog'
import type { ListChoice } from '../lists/types'
import type { ImportSettings } from './settings'
import type { ExistingMode, ImportMode } from './types'

const INPUT = 'h-9 w-full rounded-lg border border-border bg-background px-3 text-sm'
const LABEL = 'flex flex-col gap-1 text-sm font-semibold text-foreground'

const NEW_LIST = '__nieuw__'

const EXISTING: readonly { value: ExistingMode; label: string; hint: string }[] = [
  { value: 'aanvullen', label: 'Aanvullen', hint: 'Alleen lege velden van bestaande contacten worden gevuld.' },
  { value: 'overschrijven', label: 'Overschrijven', hint: 'Gevulde velden worden vervangen door die uit het bestand.' },
]

interface SettingsStepProps {
  mode: ImportMode
  settings: ImportSettings
  onSettings: (settings: ImportSettings) => void
  canContinue: boolean
  onNext: () => void
}

/** ① Instellingen voor alle rijen (spec-63 E6): categorie, labels, eigenaar, bestaande contacten, notitie. */
export function SettingsStep({ mode, settings, onSettings, canContinue, onNext }: SettingsStepProps) {
  const { membersById } = useTeamMembers()
  const members = membersById ? [...membersById.values()] : []
  const set = (patch: Partial<ImportSettings>) => onSettings({ ...settings, ...patch })

  // spec-63 E5: contacts can go onto a static list that is not archived.
  const [lists, setLists] = useState<ListChoice[] | null>(null)
  const [creating, setCreating] = useState(false)
  const chosen = settings.list
  const contacts = mode === 'contacten'
  useEffect(() => {
    if (!contacts) return
    let stale = false
    listChoices().then(
      (found) => {
        if (!stale) setLists(found.filter((list) => list.type === 'static' && !list.archived))
      },
      (caught: unknown) => {
        if (stale) return
        setLists([])
        toastFailure(caught, 'De lijsten laden mislukt.')
      },
    )
    return () => {
      stale = true
    }
  }, [contacts])

  // A list from `?lijst=` has no name yet; one that cannot take an import (gone, dynamic, archived) is dropped with a message.
  const known = chosen && lists ? lists.find((list) => list.id === chosen.id) : undefined
  const nameToFill = known && chosen && chosen.name !== known.name ? known.name : null
  const unusable = lists !== null && chosen !== null && !known
  useEffect(() => {
    if (nameToFill !== null && chosen) onSettings({ ...settings, list: { id: chosen.id, name: nameToFill } })
    else if (unusable) {
      toast.info('Deze lijst kan niet gebruikt worden voor importeren: kies een vaste lijst die niet gearchiveerd is.')
      onSettings({ ...settings, list: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameToFill, unusable])

  return (
    <section className="vonk-card flex flex-col gap-3 p-4" aria-labelledby="import-settings">
      <h2 id="import-settings" className="text-base font-semibold text-foreground">
        Instellingen voor alle rijen
      </h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className={LABEL}>
          Categorie
          <CategorySelect
            value={settings.category}
            onChange={(category) => set({ category })}
            label="Categorie voor alle rijen"
            scope="relatie"
          />
        </div>
        <label className={LABEL}>
          Eigenaar
          <select
            aria-label="Eigenaar"
            value={settings.ownerId}
            onChange={(event) => set({ ownerId: event.target.value })}
            className={INPUT}
          >
            <option value="">— Geen eigenaar</option>
            {members.map((member) => (
              <option key={member.id} value={member.id}>
                {member.display_name}
              </option>
            ))}
          </select>
        </label>
        <div className={`${LABEL} md:col-span-2`}>
          Labels toevoegen
          <div className="flex flex-wrap items-center gap-1.5">
            {settings.labels.map((label) => (
              <LabelChip
                key={label.id}
                label={label}
                onRemove={() => set({ labels: settings.labels.filter((item) => item.id !== label.id) })}
              />
            ))}
            <LabelPicker
              selected={settings.labels}
              onChange={(labels) => set({ labels })}
              label="Label toevoegen aan alle rijen"
            />
          </div>
        </div>
        <fieldset className="flex flex-col gap-1">
          <legend className="text-sm font-semibold text-foreground">
            {mode === 'contacten' ? 'Bestaande contacten' : 'Bestaande organisaties'}
          </legend>
          {EXISTING.map((option) => (
            <label key={option.value} className="flex items-start gap-2 text-sm text-foreground">
              <input
                type="radio"
                name="existing"
                value={option.value}
                checked={settings.existing === option.value}
                onChange={() => set({ existing: option.value })}
                className="mt-1"
              />
              <span>
                <span className="font-semibold">{option.label}</span>
                <span className="block text-muted-foreground">
                  {mode === 'contacten' ? option.hint : option.hint.replace('contacten', 'organisaties')}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
        {contacts ? (
          <label className={LABEL}>
            Op een lijst zetten
            <NativeSelect
              aria-label="Lijst voor alle contacten"
              value={chosen?.id ?? ''}
              onChange={(event) => {
                if (event.target.value === NEW_LIST) setCreating(true)
                else {
                  const list = lists?.find((candidate) => candidate.id === event.target.value)
                  set({ list: list ? { id: list.id, name: list.name } : null })
                }
              }}
            >
              <option value="">— Geen lijst</option>
              {chosen && !known ? <option value={chosen.id}>{chosen.name || 'Lijst laden…'}</option> : null}
              {(lists ?? []).map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
              <option value={NEW_LIST}>+ Nieuwe lijst…</option>
            </NativeSelect>
          </label>
        ) : null}
        {mode === 'contacten' ? (
          <label className={LABEL}>
            Notitie bij nieuwe contacten
            <input
              aria-label="Notitie bij nieuwe contacten"
              value={settings.note}
              maxLength={2000}
              onChange={(event) => set({ note: event.target.value })}
              placeholder="Bijvoorbeeld: ontmoet op Slush 2026"
              className={INPUT}
            />
          </label>
        ) : null}
      </div>
      <p className="text-sm text-muted-foreground">
        Neem alleen zakelijke contactgegevens op die je voor het relatiebeheer mag gebruiken.
      </p>
      <div className="flex justify-end">
        <Button type="button" onClick={onNext} disabled={!canContinue}>
          Verder
        </Button>
      </div>
      {contacts ? (
        <ListDialog
          open={creating}
          onOpenChange={setCreating}
          onSaved={(created) => {
            setCreating(false)
            if (created.type !== 'static') {
              toast.info('Een dynamische lijst kan geen import ontvangen; kies een vaste lijst.')
              return
            }
            setLists((current) => [...(current ?? []), { id: created.id, name: created.name, type: created.type, archived: false }])
            set({ list: { id: created.id, name: created.name } })
          }}
        />
      ) : null}
    </section>
  )
}
